import { getSparkleBalance } from "@/api/nfts";
import { defineStore } from "pinia";
import { cookies, setCookie, removeCookie } from "@/plugins/useCookies";
import BigNumber from "bignumber.js";
import { walletEntryPlugin } from "@particle-network/wallet";
import { formatEther, ethers } from "ethers";
import { useStorage } from "@vueuse/core";
import { getSeftCandy, getSujiBalance } from "@/api/user";
import { ParticleBridge } from "@/utils/particleNetwork";

import sujiAbi from "@/abis/suji_abi.json";

export const useUserStore = defineStore("user", () => {
  const isConnectedWallet = ref(false);
  const particleUserInfo = ref(null);
  const evmAddress = ref(null);
  const chainId = ref(0);
  const userInfo = ref();
  const balance = ref(0);
  const referralDialog = ref(false);
  const editProfileDialog = ref(false);
  const isUserProfileDialogOpen = ref(false);
  const viewedUserProfile = ref({});
  const userProfileLoading = ref(false);
  const seftZaps = ref(0);
  const seftRockets = ref(0);
  const seftSuji = ref(0);
  const seftSparkle = ref(null);

  watch([chainId, evmAddress], async ([chain, address], _, onCleanup) => {
    let stale = false;
    onCleanup(() => { stale = true; });
    balance.value = 0;
    const provider = ParticleBridge.provider;
    if (!chain || !address || !provider?.request) return;
    try {
      const result = await provider.request({
        method: "eth_getBalance",
        params: [address, "latest"],
      });
      if (!stale) balance.value = parseFloat(formatEther(result));
    } catch {
      if (!stale) balance.value = 0;
      console.warn("Wallet balance is temporarily unavailable.");
    }
  });

  const setParticleUser = async (info) => {
    particleUserInfo.value = info;
    // evmAddress.value = info ? getEVMPublicAddress() : "";
    evmAddress.value = info ? await ParticleBridge.getEvmAddress() : "";
  };

  // Always replace profile to avoid stale fields from partial merges.
  const setUserInfo = (info) => {
    if (!info || typeof info !== "object") return;

    userInfo.value = { ...info };

    console.info("Setting user info:", userInfo.value);
  };

  const submitOpenWallet = () => {
    // walletEntryPlugin.submitOpenWallet({
    //   // windowSize: "large",
    //   // topMenuType: "close",
    //   // And so on.
    // });
  };

  const openReferralDialog = () => {
    referralDialog.value = true;
  };

  const closeReferralDialog = () => {
    referralDialog.value = false;
  };

  const openEditProfileDialog = () => {
    editProfileDialog.value = true;
  };

  const closeEditProfileDialog = () => {
    editProfileDialog.value = false;
  };

  const clearViewedUserProfile = () => {
    viewedUserProfile.value = {};
    userProfileLoading.value = false;
  };

  const closeUserProfileDialog = () => {
    clearViewedUserProfile();
    isUserProfileDialogOpen.value = false;
  };

  const fetchViewedUserProfile = async () => {
    if (!viewedUserProfile.value?.uid) {
      return;
    }

    try {
      userProfileLoading.value = true;
      const { user_profile } = await getOtherProfile(
        viewedUserProfile.value.uid,
      );
      viewedUserProfile.value = user_profile;
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      userProfileLoading.value = false;
    }
  };

  const openUserProfileDialog = async (userOrId) => {
    try {
      userProfileLoading.value = true;

      if (typeof userOrId === "string" || typeof userOrId === "number") {
        const { user_profile } = await getOtherProfile(userOrId);
        viewedUserProfile.value = user_profile;
      } else if (userOrId && typeof userOrId === "object") {
        viewedUserProfile.value = userOrId;
      } else {
        throw new Error("Invalid input for openUserProfileDialog");
      }

      isUserProfileDialogOpen.value = true;
    } catch (error) {
      console.error("Error opening user profile dialog:", error);
    } finally {
      userProfileLoading.value = false;
    }
  };

  const setSeftZaps = async () => {
    try {
      const { candy } = await getSeftCandy();
      seftZaps.value = Number(candy) || 0;
    } catch (error) {
      console.error("Error fetching zaps:", error);
      seftZaps.value = 0;
    }
  };

  const setSeftSuji = async () => {
    if (!userInfo.value?.uid) {
      seftSuji.value = 0;
      return;
    }

    try {
      const { data } = await getSujiBalance(userInfo.value.uid);
      seftSuji.value = Number(data.balance) || 0;
    } catch (error) {
      console.error("Error fetching suji:", error);
      seftSuji.value = 0;
    }
  };

  let sparkleRequest = 0;
  const setSeftSparkle = async () => {
    const request = ++sparkleRequest;
    const walletAddress = evmAddress.value;
    seftSparkle.value = null;
    if (!walletAddress) return;
    try {
      const count = await getSparkleBalance(walletAddress);
      if (request === sparkleRequest && evmAddress.value === walletAddress) seftSparkle.value = count;
    } catch {
      // Unknown remains distinct from a confirmed empty collection.
    }
  };
  watch(evmAddress, () => { sparkleRequest++; seftSparkle.value = null; });

  const getSeftSujiBalance = async () => {
    try {
      const walletAddress = evmAddress.value;
      if (!walletAddress) {
        console.warn("Missing wallet address, cannot fetch SUJI balance");
        return 0;
      }

      const SUJI_SM_ADDRESS = import.meta.env.VITE_CONTRACT_SUJI_SM;
      if (!SUJI_SM_ADDRESS) {
        throw new Error("Missing SUJI contract address");
      }

      const rawProvider =
        ParticleBridge.provider ||
        (typeof window !== "undefined"
          ? window.particleAuth?.ethereum
          : window?.ethereum);

      if (!rawProvider) {
        throw new Error("No provider available for SUJI contract call");
      }

      const provider =
        rawProvider instanceof ethers.BrowserProvider
          ? rawProvider
          : new ethers.BrowserProvider(rawProvider);

      const contract = new ethers.Contract(SUJI_SM_ADDRESS, sujiAbi, provider);
      const balance = await contract.balanceOf(walletAddress);

      seftSuji.value = Number(ethers.formatUnits(balance, 18));
    } catch (error) {
      console.error("Failed to fetch on-chain SUJI balance:", error);
    }
  };
  // watch(
  //   userInfo,
  //   async (info) => {
  //     if (info) return;

  //     if (isConnectedPN.value) {
  //       console.log("User info is empty, resetting Particle user");
  //     }
  //   },
  //   { immediate: true }
  // );

  return {
    userInfo,
    particleUserInfo,
    evmAddress,
    chainId,
    isConnectedWallet,
    balance,
    referralDialog,
    editProfileDialog,
    isUserProfileDialogOpen,
    viewedUserProfile,
    userProfileLoading,
    seftZaps,
    seftRockets,
    seftSuji,
    seftSparkle,
    openEditProfileDialog,
    closeEditProfileDialog,
    openReferralDialog,
    closeReferralDialog,
    openUserProfileDialog,
    closeUserProfileDialog,
    fetchViewedUserProfile,
    clearViewedUserProfile,
    setParticleUser,
    setUserInfo,
    submitOpenWallet,
    setSeftZaps,
    setSeftSuji,
    setSeftSparkle,
    getSeftSujiBalance,
  };
});
